<script lang="ts">
export default {
  name: 'SessionExpiredModal',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import Store from '@/store';
import { useSessionExpiry } from '@/composables/useSessionExpiry';

const sessionExpired = computed<boolean>(() => Store.state.sessionExpired);

// Escape is AppModal's only dismissal path, so a no-op cancel handler is what makes
// this modal unskippable.
const ignoreDismissal = (): void => {};

const redirecting = ref(false);
const redirectFailed = ref(false);

async function reauthenticate(): Promise<void> {
  redirecting.value = true;
  redirectFailed.value = false;
  try {
    await useSessionExpiry().redirectToLogin();
  } catch {
    // This is the modal's only control, so a silent failure would strand the user.
    redirectFailed.value = true;
    redirecting.value = false;
  }
}
</script>

<template>
  <AppModal
    v-if="sessionExpired"
    :show="true"
    size="sm"
    dialog-class="session-expired-backdrop"
    :handle-cancel="ignoreDismissal">
    <template #title>
      <h1 data-testid="session-expired-modal">Your session has expired</h1>
    </template>
    <template #content>
      <p>Log in again to pick up where you left off.</p>
      <p
        v-if="redirectFailed"
        class="mt-2 text-error"
        data-testid="session-expired-error">
        Could not reach the login page. Check your connection and try again.
      </p>
    </template>
    <template #actions>
      <button
        class="btn text-white btn-primary"
        :class="{ 'btn-disabled': redirecting }"
        :disabled="redirecting"
        data-testid="session-expired-reauthenticate"
        @click="reauthenticate">
        <span
          v-if="redirecting"
          class="loading mx-2 loading-sm loading-dots"></span>
        <span v-else>Log In</span>
      </button>
    </template>
  </AppModal>
</template>

<style>
/* daisyUI 5 sets dialog::backdrop { display: none } and paints the dim on the dialog
   itself, so the blur belongs here rather than on ::backdrop. */
dialog.session-expired-backdrop {
  backdrop-filter: blur(4px);
}
</style>
